import React, { useCallback, useMemo, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { LanguageSelector } from "../../components/LanguageSelector/LanguageSelector";

import { useIntakeFormStateValidation } from "./intakeFormState";

// assets
import urBlueBlop1 from "../../assets/upper-right-blue-blop-1.svg";
import urBlueBlop2 from "../../assets/upper-right-blue-blop-2.svg";
//
import llYellow1 from "../../assets/lower-left-yellow-blop-1.svg";
import llYellow2 from "../../assets/lower-left-yellow-blop-2.svg";
import llYellow3 from "../../assets/lower-left-yellow-blop-3.svg";

export interface IntakeOutletContext {
  onSubmit: () => void;
}

// NOTE: hardcoding isn't great but it doesnt look like react router has any good way
// to check if the next route exists lol.
const MAX_STEP_IDX = 5;

/**
 * BACKGROUND IMAGES
 */

interface PerPageConfig {
  upperRightBlop: any | undefined;
  lowerLeftBlop: any | undefined;
}

const perPageConfig = {
  start: { lowerLeftBlop: llYellow1, upperRightBlop: undefined },
  config1: { lowerLeftBlop: undefined, upperRightBlop: urBlueBlop1 },
  config2: { lowerLeftBlop: llYellow3, upperRightBlop: urBlueBlop1 },
  config3: { lowerLeftBlop: llYellow2, upperRightBlop: urBlueBlop2 },
};

const useConfigForPage = (): PerPageConfig => {
  const { pathname } = useLocation();

  return useMemo(() => {
    if (pathname.includes("intake/1")) {
      return perPageConfig.config1;
    }

    if (pathname.includes("intake/2")) {
      return perPageConfig.config3;
    }

    if (pathname.includes("intake/3")) {
      return perPageConfig.config2;
    }

    if (pathname.includes("intake/4")) {
      return perPageConfig.config3;
    }

    if (pathname.includes("intake/5")) {
      return perPageConfig.config2;
    }

    if (pathname.includes("intake/submit")) {
      return perPageConfig.config3;
    }

    return perPageConfig.start;
  }, [pathname]);
};

const BackgroundImages = React.memo(() => {
  const { lowerLeftBlop: llSrc, upperRightBlop: urSrc } = useConfigForPage();

  return (
    <>
      {/* scale width automatically based on height. but only scale up to a certain point (i.e. specify max width) */}
      {urSrc ? <img src={urSrc} className="fixed right-0 top-0 -z-10 h-36 md:h-96 md:max-w-96" /> : null}
      {llSrc ? <img src={llSrc} className="fixed bottom-0 left-0 -z-10 h-36 md:h-96 md:max-w-96" /> : null}
    </>
  );
});

BackgroundImages.displayName = "BackgroundImages";

export const IntakeFormLayout = React.memo(() => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentStepIdx = useMemo(() => {
    const currentStepIdx = parseInt(pathname.replace("/intake/", ""));
    return isNaN(currentStepIdx) ? 0 : currentStepIdx;
  }, [pathname]);

  const errors = useIntakeFormStateValidation();

  // NOTE: redirect to consent screen if they passed it (e.g. via URL bar),
  // cleaned local storage, etc.
  // This is IMPORTANT! bc we can't collect data unless they agree to our terms
  useEffect(() => {
    const isPastConsentStep = currentStepIdx > 1;
    const didNotGiveConsent = errors.includes("consent");
    if (isPastConsentStep && didNotGiveConsent) {
      navigate("/intake/1");
    }
  }, [currentStepIdx, errors, navigate]);

  const handleGoToNext = useCallback(() => {
    if (currentStepIdx === MAX_STEP_IDX) {
      navigate("/intake/submit");
    } else {
      navigate(`/intake/${currentStepIdx + 1}`);
    }
  }, [currentStepIdx, navigate]);

  const formFlowControlState = useMemo(() => {
    return {
      onSubmit: handleGoToNext,
    };
  }, [handleGoToNext]);

  return (
    <div>
      {currentStepIdx === 0 && !pathname.includes("intake/submit") ? (
        <div className="fixed right-4 top-4 md:right-8">
          <LanguageSelector />
        </div>
      ) : null}
      <div className="flex flex-row items-center justify-center">
        {/* <Outlet /> is where child routes are rendered. Pass form state, callbacks, etc. in FormState */}
        <Outlet context={formFlowControlState} />
      </div>
      <BackgroundImages />
    </div>
  );
});

IntakeFormLayout.displayName = "IntakeFormLayout";
